import React, { useState, useEffect, createContext, useContext, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import InstallBanner from './components/InstallBanner';
import UpdateBanner from './components/UpdateBanner';

import { onAuthChange, logOut } from './firebase/auth';
import { getUserById, getTapeFollowingIds, getPrivateAccessOwnerIds } from './firebase/firestore';

// Route-level code splitting — each page loads only when first visited.
// After first visit the service worker caches each chunk, so navigation is instant.
const Login        = lazy(() => import('./pages/Login'));
const Signup       = lazy(() => import('./pages/Signup'));
const Home         = lazy(() => import('./pages/Home'));
const Profile      = lazy(() => import('./pages/Profile'));
const EditProfile  = lazy(() => import('./pages/EditProfile'));
const Search       = lazy(() => import('./pages/Search'));
const Upload       = lazy(() => import('./pages/Upload'));
const SingleVideo  = lazy(() => import('./pages/SingleVideo'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Settings     = lazy(() => import('./pages/Settings'));
const ProfileFeed  = lazy(() => import('./pages/ProfileFeed'));
const Followers    = lazy(() => import('./pages/Followers'));
const Chat         = lazy(() => import('./pages/Chat'));

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="loading-content">
        <div className="loading-logo">📼 <span>TAPES</span></div>
        <div className="loading-spinner" />
      </div>
    </div>
  );
}

// Minimal inline spinner used as Suspense fallback between route transitions.
// Avoids re-showing the full splash when the SW already has the chunk cached.
function PageSpinner() {
  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="loading-spinner" />
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [followingIds, setFollowingIds] = useState([]);
  const [privateAccessOwnerIds, setPrivateAccessOwnerIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthChange(async (firebaseUser) => {
      try {
        if (firebaseUser) {
          setUser(firebaseUser);
          const profile = await getUserById(firebaseUser.uid);
          setUserProfile(profile);
          const [ids, privateIds] = await Promise.all([
            getTapeFollowingIds(firebaseUser.uid),
            getPrivateAccessOwnerIds(firebaseUser.uid),
          ]);
          setFollowingIds(ids);
          setPrivateAccessOwnerIds(privateIds);
        } else {
          setUser(null);
          setUserProfile(null);
          setFollowingIds([]);
          setPrivateAccessOwnerIds([]);
        }
      } catch (e) {
        console.error('Auth init error:', e);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const refreshProfile = async () => {
    if (user) {
      const profile = await getUserById(user.uid);
      setUserProfile(profile);
    }
  };

  const refreshFollowing = async () => {
    if (user) {
      const ids = await getTapeFollowingIds(user.uid);
      setFollowingIds(ids);
    }
  };

  const refreshPrivateAccess = async () => {
    if (user) {
      const ids = await getPrivateAccessOwnerIds(user.uid);
      setPrivateAccessOwnerIds(ids);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <AuthContext.Provider value={{ user, userProfile, followingIds, privateAccessOwnerIds, logout: logOut, refreshProfile, refreshFollowing, refreshPrivateAccess }}>
      <div className="app">
        <UpdateBanner />
        <InstallBanner />
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            <Route path="/login"        element={user ? <Navigate to="/" /> : <Login />} />
            <Route path="/signup"       element={user ? <Navigate to="/" /> : <Signup />} />
            <Route path="/"             element={<Home />} />
            <Route path="/search"       element={<Search />} />
            <Route path="/upload"       element={user ? <Upload /> : <Navigate to="/login" />} />
            <Route path="/notifications" element={user ? <Notifications /> : <Navigate to="/login" />} />
            <Route path="/settings"     element={user ? <Settings /> : <Navigate to="/login" />} />
            <Route path="/edit-profile" element={user ? <EditProfile /> : <Navigate to="/login" />} />
            <Route path="/video/:videoId" element={<SingleVideo />} />
            <Route path="/profile-feed" element={<ProfileFeed />} />
            <Route path="/followers"    element={user ? <Followers /> : <Navigate to="/login" />} />
            <Route path="/chat/:convId" element={user ? <Chat /> : <Navigate to="/login" />} />
            <Route path="/:username"    element={<Profile />} />
          </Routes>
        </Suspense>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
